import { useState, useEffect } from "react";
import { Modal, Button, Image, Spinner, Badge } from "react-bootstrap";
import { MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import communityService from "../../../../api/communityService";
import { getUser } from "../../../../utils/auth";
import { toast } from "sonner";
import { FiX } from "react-icons/fi";
import { ReactComponent as MapPin } from "../../../../assets/images/nursing-locations.svg";
import { ReactComponent as Award } from "../../../../assets/images/nursing-role.svg";
import { ReactComponent as Calendar } from "../../../../assets/images/nursing-exp.svg";
const UserProfileModal = ({
  show,
  onHide,
  userId,
  userName,
  userImage,
  userState,
}) => {
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const navigate = useNavigate();

  const currentUser = getUser();
  const isOwnProfile = currentUser?.id === userId;

  // Fetch user profile details
  const fetchUserProfile = async () => {
    if (!userId || isOwnProfile) return;

    setLoading(true);
    try {
      const response = await communityService.getUserQuickInfo(userId);

      if (response?.data?.success) {
        const userData = response.data.data;
        setUserProfile({
          id: userId,
          name:
            userData.full_name ||
            `${userData.first_name || ""} ${userData.last_name || ""}`.trim() ||
            userName,
          nursingRole: userData.nursing_role?.role_title || "Not provided", // Changed from email to nursing role
          city: userData.city || "Not provided", // City not in API response
          state: userData.state || userState || "Not provided", // State not in API response
          yearsOfExperience:
            userData.nursing_experience?.experience_title || "Not provided",
          profileImage: userData.profile_photo_path || userImage,
        });
      } else {
        // Fallback to basic data if API fails
        setUserProfile({
          id: userId,
          name: userName,
          nursingRole: "Not provided", // Changed from email to nursing role
          city: "Not provided",
          state: userState || "Not provided",
          yearsOfExperience: "Not provided",
          profileImage: userImage,
        });
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);

      // Fallback to basic data on error
      setUserProfile({
        id: userId,
        name: userName,
        nursingRole: "Not provided", // Changed from email to nursing role
        city: "Not provided",
        state: userState || "Not provided",
        yearsOfExperience: "Not provided",
        profileImage: userImage,
      });

      toast.error("Failed to load user profile details");
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is following this user
  const checkFollowStatus = async () => {
    if (!userId || isOwnProfile) return;

    try {
      const response = await communityService.getFollowStatus(userId);

      if (response?.data?.success) {
        const isUserFollowed = response.data.data?.is_following || false;
        setIsFollowing(isUserFollowed);
      }
    } catch (error) {
      console.error("Error checking follow status:", error);
    }
  };

  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!userId) return;

    setFollowLoading(true);
    try {
      const response = await communityService.follow(userId);

      if (response?.data?.success) {
        setIsFollowing(!isFollowing);
        toast.success(
          response?.data?.message ||
            (isFollowing ? "Unfollowed successfully" : "Followed successfully"),
        );
      }
    } catch (error) {
      console.error("Error following/unfollowing user:", error);
      toast.error(error?.response?.data?.message || "Failed to follow user");
    } finally {
      setFollowLoading(false);
    }
  };

  // Handle message button click
  const handleMessageClick = () => {
    // Close the modal first
    onHide();

    // Navigate to messaging page with the user ID and user data in state
    navigate(`/messaging/${userId}`, {
      state: {
        pendingUser: {
          id: userId,
          first_name: userName?.split(" ")[0] || userName,
          last_name:
            userName
              ?.split(" ")
              .slice(1)
              .join(" ") || "",
          profile_photo_path: userImage,
          state: userState,
        },
      },
    });
  };

  useEffect(() => {
    if (show && userId) {
      fetchUserProfile();
      checkFollowStatus();
    }
  }, [show, userId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setUserProfile(null);
      setIsFollowing(false);
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="md"
      backdrop={true}
      dialogClassName="custom-modal profile-other-details-modal user-profile-modal"
      style={{ zIndex: 100000 }}
    >
      <Modal.Header>
        <h5 className="modal-title">User Profile</h5>
        <Button className="close-btn" onClick={onHide}>
          <FiX size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          padding: "16px",
        }}
      >
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="mt-2 mb-0 small">Loading profile...</p>
          </div>
        ) : userProfile ? (
          <div className="user-profile-content">
            {/* Profile Header */}
            <div className="text-center mb-3">
              <Image
                src={
                  userProfile.profileImage
                    ? `${process.env.REACT_APP_API_BASE_URL}/uploads/user_profile/${userProfile.profileImage}`
                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        userProfile.name || "User",
                      )}&background=0195D3&color=fff&size=80`
                }
                alt={userProfile.name}
                roundedCircle
                width={80}
                height={80}
                className="mb-2"
                style={{ objectFit: "cover" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    userProfile.name || "User",
                  )}&background=0195D3&color=fff&size=80`;
                }}
              />
              <h5
                className="mb-1"
                style={{ fontSize: "15px", fontWeight: "600" }}
              >
                {userProfile.name}
              </h5>

              {/* Follow Status Tag or Follow Button */}
              <div className="mb-3">
                {!isOwnProfile && (
                  <>
                    {isFollowing ? (
                      <Badge
                        bg="primary"
                        className="me-2"
                        style={{
                          fontSize: "11px",
                          padding: "4px 10px",
                          borderRadius: "15px",
                        }}
                      >
                        Following
                      </Badge>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleFollowToggle}
                        disabled={followLoading}
                        className="me-2"
                        style={{
                          fontSize: "11px",
                          padding: "4px 12px",
                          borderRadius: "15px",
                        }}
                      >
                        {followLoading ? "..." : "Follow"}
                      </Button>
                    )}

                    {/* Message Button */}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleMessageClick}
                      style={{
                        fontSize: "11px",
                        padding: "4px 12px",
                        borderRadius: "15px",
                      }}
                    >
                      <MessageCircle size={12} className="me-1" />
                      Message
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Profile Details */}
            <div className="profile-details">
              <div className="detail-item mb-2">
                <div className="d-flex align-items-center mb-1">
                  <Award size={18} className="me-2 text-muted" />
                  <strong style={{ fontSize: "13px" }}>Nursing Role</strong>
                </div>
                <div className="ms-4 text-muted" style={{ fontSize: "12px" }}>
                  {userProfile.nursingRole}
                </div>
              </div>

              <div className="detail-item mb-2">
                <div className="d-flex align-items-center mb-1">
                  <MapPin size={18} className="me-2 text-muted" />
                  <strong style={{ fontSize: "13px" }}>Location</strong>
                </div>
                <div className="ms-4 text-muted" style={{ fontSize: "12px" }}>
                  {userProfile.city}, {userProfile.state}
                </div>
              </div>

              <div className="detail-item mb-0">
                <div className="d-flex align-items-center mb-1">
                  <Calendar size={18} className="me-2 text-muted" />
                  <strong style={{ fontSize: "13px" }}>
                    Years of Experience
                  </strong>
                </div>
                <div className="ms-4 text-muted" style={{ fontSize: "12px" }}>
                  {userProfile.yearsOfExperience}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-3">
            <p className="mb-0 small">Unable to load user profile</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UserProfileModal;
